// @ts-strict-ignore
import {
  Box,
  Tooltip,
  useSnackBar,
} from '@usealloy/component-library';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Position } from 'react-flow-renderer';

import {
  selectActionByPublicId,
  selectNodeState,
  selectContainerRelationship,
  selectWorkflowData,
  selectWorkflowProps,
  selectWorkflowSupportsCopyPaste,
  selectUnsetTagNodeIds,
  selectIsTagSwapMode,
  selectMergeDiff,
  selectSnippetMode,
  selectSnippetActiveOperation,
  selectSnippetInsertionErrors,
  selectDiffedActions,
  selectDiffMode,
} from 'flow/store/redux/selectors';
import { setHoveredNodeId } from 'flow/store/redux/slice';

import type {
  NodeState,
  WorkflowNodeProps,
} from 'flow/service/graph';
import {
  COLUMN_SPACING,
  NODE_WIDTH,
  workflowNodeType,
} from 'flow/service/graph';

import { useDelayedState } from 'flow/editor/hooks';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  StyledLeftHandle,
  StyledBaseNode,
  StyledRightHandle,
  StyledNodeText,
  StyledNodeHoverArea,
  StyledEvaluationSetTagNodeIndicator,
  StyledAddedOrModifiedIndicator,
  StyledSnippetInsertionErrorIndicator,
} from './BaseNode.styles';
import NodeConnector from './NodeConnector';
import {
  canDuplicateNode,
  doesActionSupportTooltip,
  doesNodeTypeSupportConnector,
  doesNodeTypeSupportMenu,
  getCanHoverOnNode,
  getDropdownItems,
  isAddedOrModifiedBySnippetUpdate,
  showNodeMenu,
} from './helpers';
import { formatColor } from '../../../helpers';
import NodeDropdownMenu from './NodeDropdownMenu';
import NodeTooltip from './NodeTooltip';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import { useEditorAccess } from '../../../../context';
import ConfirmNodeDeletionModal from './ConfirmNodeDeletionModal';
import { isActionOfType } from 'flow/service/actions';
import { getIsEditable } from 'flow/service/general';
import { UserContext } from '~/dashboard/context';
import { updateCustomListVersionToActiveThunk } from 'flow/store/redux/thunks/services';
import UpdateToActiveListModal from '~/dashboard/views/settings/customLists/modals/UpdateCustomListToActiveVersion';
import { selectThresholdOutputResults } from 'flow/store/redux/selectors/workflowFeatures';
import { useCopyPasteHandlers } from 'flow/editor/components/SnippetCopyPaste/useCopyPasteHandlers';
import { isSnippetChildAction } from 'flow/service/actions/snippets';
import useAPI from '~/helpers/api-new/context';
import { getMergeDiffOperation } from 'flow/service/changelog/formatters/mergeDropdownFormatter/helpers';

type BaseNodeProps = {
  icon?: JSX.Element;
  label?: string | React.ReactNode;
  children?: JSX.Element;
  construction?: boolean;
  stateStyle: { baseBackgroundColor: string } & Partial<Record<NodeState, React.CSSProperties>>;
  menuTriggerStyle?: React.CSSProperties;
  isOutsideCanvas?: boolean;
  isChildNode?: boolean;
  style?: React.CSSProperties;
  disable?: {
    nodeConnector?: boolean;
    nodeMenu?: boolean;
    nodeMenuHoverState?: boolean;
    tooltip?: boolean;
  };
  handleStyle?: {
    left?: React.CSSProperties;
    right?: React.CSSProperties;
  };
};

type SupportedNodeProps = Pick<WorkflowNodeProps, 'id' | 'type'>;

function BaseNode({
  icon = null,
  id,
  label,
  type,
  children,
  construction = false,
  stateStyle,
  menuTriggerStyle,
  isOutsideCanvas = false,
  isChildNode = false,
  disable,
  style,
  handleStyle,
}: BaseNodeProps & SupportedNodeProps) {
  const { featureFlags, typhonDomain, permissions } = useContext(UserContext);
  const { 'CREATE:SnippetMetadata:null': canCreateSnippetPermission } = permissions;
  const api = useAPI();
  const addSnackBar = useSnackBar();
  const { handleCopy } = useCopyPasteHandlers();

  const dispatch = useAppDispatch();
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);
  const wfSupportsCopyPaste = useAppSelector(selectWorkflowSupportsCopyPaste);
  const nodeState = useAppSelector(state => selectNodeState(state, id));
  const containerRelationship = useAppSelector(state => selectContainerRelationship(state, id));
  // id might be null under _some_ sporadic edge cases
  const action = useAppSelector(selectActionByPublicId(id));
  const unsetTagNodeIds = useAppSelector(selectUnsetTagNodeIds);
  const isTagSwapMode = useAppSelector(selectIsTagSwapMode);
  const mergeDiff = useAppSelector(selectMergeDiff);
  const snippetModeState = useAppSelector(selectSnippetMode);
  const { operation: snippetOperation } = useAppSelector(selectSnippetActiveOperation);
  const snippetInsertionErrors = useAppSelector(selectSnippetInsertionErrors);
  const thresholdOutputResults = useAppSelector(selectThresholdOutputResults);

  const mergeDiffOperation = getMergeDiffOperation(mergeDiff, id);
  const showSnippetAddedModifiedIndicator = isAddedOrModifiedBySnippetUpdate({
    snippetOperation,
    mergeDiffOperation,
  }) && !isActionOfType(action, ActionType.SNIPPET);

  const showSnippetInsertionErrorIndicator = useMemo(
    () => snippetInsertionErrors.some(error => error.actionPublicId === id),
    [snippetInsertionErrors, id],
  );

  const isTagNodeSet = useMemo(() => thresholdOutputResults
    && !(unsetTagNodeIds.includes(id)), [id, unsetTagNodeIds, thresholdOutputResults]);

  const [isDropdownTriggerHovered, setIsDropdownTriggerHovered] = useState<boolean>(false);
  const [isNodeConnectorHovered, setIsNodeConnectorHovered] = useState<boolean>(false);
  const [isNodeDropdownVisible, setIsNodeDropdownVisible] = useState<boolean>(false);
  const [isNodeBeingUpdated, setIsNodeBeingUpdated] = useState<boolean>(false);

  const isSnippetChild = isSnippetChildAction(action);
  const { editorMode } = useEditorAccess();

  const isEditorEditable = getIsEditable(editorMode);
  const isEditingDisabled = isEditorEditable && !snippetModeState;

  const {
    reset: closeRuleTooltip, value: isRuleTooltipOpen, set: setRuleTooltipState,
  } = useDelayedState<boolean>({
    initialState: false,
    valueSetDelay: 750,
  });

  const [nodeDeletionChildActionCounts, setNodeDeletionChildActionCounts] = useState<number>(0);

  const resetNodeChildActionCounts = () => setNodeDeletionChildActionCounts(0);

  const handleUpdateCustomListVersion = useCallback(() => {
    dispatch(updateCustomListVersionToActiveThunk(id));
    setIsNodeBeingUpdated(false);
  }, [dispatch, id]);


  // Close tooltip immediately when entering:
  //  - tag swap mode
  //  - paste review mode where we see the merge diff compared on the graph
  //  - or the node in the process of being updated
  useEffect(() => {
    if (isTagSwapMode.isActive || mergeDiff || isNodeBeingUpdated) {
      closeRuleTooltip();
    }
  }, [isTagSwapMode.isActive, closeRuleTooltip, isNodeBeingUpdated, mergeDiff]);

  const canDuplicate = (
    Boolean(featureFlags.workflowDuplicateNode) && canDuplicateNode(type)
  );
  const canCreateSnippet = Boolean(canCreateSnippetPermission);

  const dropdownItems = useMemo(() => getDropdownItems({
    addSnackBar,
    dispatch,
    canCreateSnippet,
    nodeId: id,
    workflowData,
    workflowProps,
    setNodeDeletionChildActionCounts,
    nodeType: type,
    onUpdateAction: setIsNodeBeingUpdated,
    permissions,
    closeRuleTooltip,
    onCopy: handleCopy,
    canDuplicate,
    libraryApi: api.library,
    typhonDomain,
    featureFlags,
    wfSupportsCopyPaste,
  }), [
    addSnackBar,
    dispatch,
    canCreateSnippet,
    id,
    workflowData,
    workflowProps,
    type,
    closeRuleTooltip,
    wfSupportsCopyPaste,
    canDuplicate,
    handleCopy,
    api.library,
    typhonDomain,
    featureFlags,
    permissions,
  ]);

  const openRuleTooltip = useCallback(() => {
    const tooltipState = !showNodeMenu(nodeState, type)
      && (doesActionSupportTooltip(action)
      || (nodeState === 'hovered' || nodeState === 'focused-hovered')
      || isDropdownTriggerHovered
      || isNodeConnectorHovered);

    setRuleTooltipState(tooltipState);
  }, [
    action, isDropdownTriggerHovered, isNodeConnectorHovered,
    type, nodeState, setRuleTooltipState,
  ]);

  const handleMouseEnter = useCallback(() => {
    dispatch(setHoveredNodeId(id));

    // Don't open tooltip when in tag swap mode or node in the process of being updated
    if (!isTagSwapMode.isActive && !isNodeBeingUpdated) {
      openRuleTooltip();
    }
  }, [dispatch, id, openRuleTooltip, isTagSwapMode, isNodeBeingUpdated]);


  const handleMouseLeave = useCallback(() => {
    if (!isNodeDropdownVisible) {
      dispatch(setHoveredNodeId(null));
    }
    closeRuleTooltip();
  }, [dispatch, isNodeDropdownVisible, closeRuleTooltip]);

  const TooltipContent = useMemo<React.ReactNode>(
    /**
     * there is a quirk in the react-flow internals causing Node objects to be created
     * for an Action that has been immediately deleted. to be safe, we only want to render
     * the tooltip when there is an Action.
     */
    () => (action ? <NodeTooltip action={action} /> : null),
    [action],
  );

  const { baseBackgroundColor = 'var(--cl-gray-200)' } = stateStyle;

  const dataTestId = `workflowNode-${type}-${id}`;
  const isInDiffMode = useAppSelector(selectDiffMode);
  const diffedActions = useAppSelector(selectDiffedActions);

  /**
   * construction nodes are invisible nodes that other nodes can connect to
   */
  if (construction && !isOutsideCanvas) {
    return (
      <StyledBaseNode
        workflowDiffOperation={diffedActions?.[id] ?? null}
        baseBackgroundColor={baseBackgroundColor}
        nodeState="normal"
        data-testid={dataTestId}
        data-value={label}
      >
        <StyledLeftHandle
          type="target"
          position={Position.Left}
          style={handleStyle?.left}
        />
        <StyledRightHandle
          type="source"
          position={Position.Right}
          style={handleStyle?.right}
        />
      </StyledBaseNode>
    );
  }

  const canHover = getCanHoverOnNode(mergeDiff, id);
  // Shared by the add-child/link connector and its enlarged hover hit-box below.
  const canShowNodeConnector = isEditorEditable
    && !disable?.nodeConnector
    && doesNodeTypeSupportConnector(type);

  return (
    <Tooltip
      align="start"
      hide={disable?.tooltip || isActionOfType(action, ActionType.EXIT)}
      content={TooltipContent}
      delayDuration={750}
      disableHoverableContent
      doHideArrow
      open={isRuleTooltipOpen}
      side="bottom"
      style={{ maxWidth: `${(NODE_WIDTH * 2) + COLUMN_SPACING}px` }}
      variant="secondary"
    >
      <StyledBaseNode
        workflowDiffOperation={diffedActions[id] ?? null}
        isDiffMode={isInDiffMode}
        data-dd-privacy="allow"
        baseBackgroundColor={formatColor(baseBackgroundColor)}
        nodeState={nodeState}
        style={{
          ...style,
          ...stateStyle[nodeState],
        }}
        onMouseEnter={canHover ? handleMouseEnter : undefined}
        onMouseLeave={canHover ? handleMouseLeave : undefined}
        isOutsideCanvas={isOutsideCanvas}
        isChildNode={isChildNode}
        isFolderNode={type === workflowNodeType.folder}
        containerRelationship={containerRelationship}
        mergeDiffOperation={mergeDiffOperation}
        snippetOperation={snippetOperation}
        data-testid={dataTestId}
        data-value={label}
        data-dd-action-name={`workflow-node-${type}`}
      >
        {/* Extra hit-box so approaching a node reveals its add-child/link connector.
            First child + z-index:-1 keeps the node body and connector clickable. */}
        {canHover
          && !isOutsideCanvas
          && !isSnippetChild
          && canShowNodeConnector
          && (
            <StyledNodeHoverArea
              aria-hidden
              data-testid={`workflowNode-hoverArea-${id}`}
            />
          )}
        {!isOutsideCanvas && (
          <StyledLeftHandle
            type="target"
            position={Position.Left}
            style={handleStyle?.left}
          />
        )}
        <Box gap={1} alignItems="center" justifyContent="flex-start">
          {icon}
          {typeof label === 'string' ? (
            <StyledNodeText
              isChildNode={isChildNode}
              isOutsideCanvas={isOutsideCanvas}
              nodeState={nodeState}
            >
              {label}
            </StyledNodeText>
          ) : (
            label
          )}
        </Box>

        {children}

        {isEditingDisabled
          && !isSnippetChild
          && !disable?.nodeMenu
          && (disable?.nodeMenuHoverState
            ? doesNodeTypeSupportMenu(type)
            : showNodeMenu(nodeState, type)
          )
          && (
            <NodeDropdownMenu
              data-dd-action-name={`workflow-node-${type}-dropdown`}
              backgroundColor={baseBackgroundColor}
              onDropdownVisibilityChange={isOpen => {
                if (!isOpen && !isNodeConnectorHovered) {
                  dispatch(setHoveredNodeId(null));
                }
                setIsNodeDropdownVisible(isOpen);
              }}
              label={label}
              dropdownItems={dropdownItems}
              onTriggerMouseEnter={() => {
                setIsDropdownTriggerHovered(true);
                closeRuleTooltip();
              }}
              onTriggerMouseLeave={() => {
                setIsDropdownTriggerHovered(false);
                openRuleTooltip();
              }}
              menuTriggerStyle={menuTriggerStyle}
            />
          )}

        {!isEditingDisabled && isTagNodeSet && featureFlags?.ruleExplainabilityPhase >= 4 && (
          <StyledEvaluationSetTagNodeIndicator />
        )}

        {showSnippetAddedModifiedIndicator && (
          <StyledAddedOrModifiedIndicator />
        )}

        {showSnippetInsertionErrorIndicator && (
          <StyledSnippetInsertionErrorIndicator />
        )}

        {nodeDeletionChildActionCounts > 0 && (
          <ConfirmNodeDeletionModal
            nodeDeletionChildActionCounts={nodeDeletionChildActionCounts}
            resetNodeChildActionCounts={resetNodeChildActionCounts}
            id={id}
            isWorkflowHistoryOn={Boolean(featureFlags?.workflowEditorUndoRedo)}
          />
        )}

        {isNodeBeingUpdated && (
          <UpdateToActiveListModal
            isOpen={isNodeBeingUpdated}
            onClose={() => setIsNodeBeingUpdated(false)}
            onUpdate={handleUpdateCustomListVersion}
            nodeId={id}
          />
        )}

        {!isOutsideCanvas && (
          <StyledRightHandle
            type="source"
            position={Position.Right}
            style={handleStyle?.right}
          />
        )}

        {canShowNodeConnector
          && (nodeState === 'hovered' || nodeState === 'focused-hovered')
          && (
            <NodeConnector
              connectorParentNodeId={id}
              onMouseEnter={() => {
                setIsNodeConnectorHovered(true);
                closeRuleTooltip();
              }}
              onMouseLeave={() => {
                setIsNodeConnectorHovered(false);
                openRuleTooltip();
              }}
            />
          )}
      </StyledBaseNode>
    </Tooltip>
  );
}


export default React.memo(BaseNode);
